/**
 * Dados (docs/spec/settings.md): a aparência dos meus dados (as cores e a textura que vão com cada
 * rolagem e que os outros veem) e os efeitos especiais (docs/spec/dice-3d.md). Antes era o diálogo
 * "Personalizar dados"; agora grava a cada mudança, como o resto das preferências.
 */
import { useId, useState } from 'react';
import { formatBytes } from '../bytes.js';
import { useT } from '../i18n/index.js';
import { DEFAULT_SETTINGS } from '../settings.js';
import { useApp } from '../store.js';
import { Button, ComboBox, Slider, Switch } from '../ui/controls.js';
import { ImagePickerDialog } from '../ui/ImagePicker.js';
import {
  DEFAULT_DICE_EFFECTS,
  DICE_EFFECT_IDS,
  DICE_TEXTURE_LIMITS,
  EFFECT_DICE,
  MAX_DICE_EFFECT_RULES,
  type DiceEffectId,
  type DiceEffectRule,
  type EffectDieSides,
} from '../shared.js';
import { DicePreview, type PreviewRequest } from '../dice/DicePreview.js';
import { useDiceTexturePreview } from '../dice/texture.js';
import { useSavePrefs } from './useSavePrefs.js';

/** As cores prontas do corpo e do número; a última amostra é uma cor livre. */
const BODY_SWATCHES = ['#9db1ff', '#e0474c', '#e8833a', '#e3c13b', '#4caf50', '#2fa6a0', '#8a5ad6', '#d65aa8', '#f2f2f2', '#2b2b2b'];
const NUMBER_SWATCHES = ['#ffe066', '#f2f2f2', '#1a1a1a', '#ef6070', '#8fd1a4', '#9ec3ff'];

/** As imagens que servem de textura e a redução no envio (as do Cloud fora disso ficam apagadas). */
const TEXTURE_RULES = { types: DICE_TEXTURE_LIMITS.types, maxBytes: DICE_TEXTURE_LIMITS.maxBytes, maxSide: DICE_TEXTURE_LIMITS.side };

/** As dez faces da frente do d20 desenhado, cada uma com a caixa dela (a imagem da textura cobre cada face). */
const D20_FACES = [
  [[50, 22], [76, 66], [24, 66]],
  [[50, 4], [10, 27], [50, 22]],
  [[50, 4], [50, 22], [90, 27]],
  [[10, 27], [24, 66], [50, 22]],
  [[90, 27], [50, 22], [76, 66]],
  [[10, 27], [10, 73], [24, 66]],
  [[90, 27], [76, 66], [90, 73]],
  [[10, 73], [50, 96], [24, 66]],
  [[90, 73], [76, 66], [50, 96]],
  [[24, 66], [76, 66], [50, 96]],
].map((corners) => {
  const xs = corners.map(([x]) => x!);
  const ys = corners.map(([, y]) => y!);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { points: corners.map((corner) => corner.join(',')).join(' '), x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
});

/**
 * O d20 desenhado, com as cores escolhidas (o 3D fica para o teste dos efeitos). Com a textura, cada
 * face mostra a imagem centrada nela, no zoom escolhido (`scale`: em 1, a imagem cobre a face; abaixo,
 * o padrão se repete), como no 3D, e o número ganha o contorno escuro.
 */
function D20({ body, ink, texture, scale, label }: { body: string; ink: string; texture: string | null; scale: number; label: string }) {
  const pattern = useId();
  return (
    <svg className="rd-prefs__d20" viewBox="0 0 100 100" width="112" height="112" role="img" aria-label={label}>
      <polygon points="50,4 90,27 90,73 50,96 10,73 10,27" fill={body} stroke="rgb(0 0 0 / 45%)" strokeWidth="2" />
      {texture ? (
        <>
          <defs>
            {D20_FACES.map((face, i) => {
              const size = Math.max(face.width, face.height) * scale;
              return (
                <pattern
                  key={face.points}
                  id={`${pattern}-${i}`}
                  patternUnits="userSpaceOnUse"
                  x={face.x + (face.width - size) / 2}
                  y={face.y + (face.height - size) / 2}
                  width={size}
                  height={size}
                >
                  <image href={texture} width={size} height={size} preserveAspectRatio="xMidYMid slice" />
                </pattern>
              );
            })}
          </defs>
          {D20_FACES.map((face, i) => (
            <polygon key={face.points} points={face.points} fill={`url(#${pattern}-${i})`} />
          ))}
          <polygon points="50,4 90,27 90,73 50,96 10,73 10,27" fill="none" stroke="rgb(0 0 0 / 45%)" strokeWidth="2" />
        </>
      ) : null}
      <polygon points="50,22 76,66 24,66" fill="rgb(0 0 0 / 10%)" stroke="rgb(0 0 0 / 40%)" strokeWidth="1.5" />
      <path d="M50 22L10 27M50 22L90 27M76 66L90 27M76 66L90 73M76 66L50 96M24 66L50 96M24 66L10 73M24 66L10 27M50 22L50 4" stroke="rgb(0 0 0 / 35%)" strokeWidth="1.2" fill="none" />
      <text
        x="50"
        y="58"
        textAnchor="middle"
        fontFamily="var(--font-title)"
        fontWeight="900"
        fontSize="19"
        fill={ink}
        {...(texture ? { stroke: 'rgb(0 0 0 / 80%)', strokeWidth: 3, strokeLinejoin: 'round' as const, paintOrder: 'stroke' } : {})}
      >
        20
      </text>
    </svg>
  );
}

/**
 * A textura dos meus dados: "Escolher imagem…" abre o seletor da capa da mesa (o Desk Cloud, o
 * computador e o endereço da web, que vira uma cópia no Cloud) e "Remover" volta às cores. Com a
 * textura, o "Tamanho da estampa" é o zoom: o deslizante anda em potências de 2, de 25% a 400%, com
 * 100% no meio. Avisa quando a imagem não abre e quando a última rolagem saiu sem ela (o servidor
 * recusou).
 */
function DiceTexture({
  value,
  preview,
  scale,
  disabled,
  onChange,
  onScale,
}: {
  value: string | null;
  preview: string | null | 'error';
  scale: number;
  disabled: boolean;
  onChange: (ref: string | null) => void;
  onScale: (scale: number) => void;
}) {
  const t = useT();
  const labelId = useId();
  const scaleId = useId();
  const [picking, setPicking] = useState(false);
  const refused = useApp((s) => s.diceTextureRefused);
  const warning = !value
    ? null
    : preview === 'error'
      ? t('prefs.dice.texture.broken')
      : refused === value
        ? t('prefs.dice.texture.refused', { max: formatBytes(DICE_TEXTURE_LIMITS.maxBytes) })
        : null;
  return (
    <div className="rd-prefs__swatchGroup" role="group" aria-labelledby={labelId}>
      <span className="rd-prefs__swatchLabel" id={labelId}>
        {t('prefs.dice.texture')}
      </span>
      <div className="rd-prefs__textureActions">
        <Button variant="secondary" disabled={disabled} onClick={() => setPicking(true)}>
          {t('prefs.dice.texture.pick')}
        </Button>
        {value ? (
          <Button variant="secondary" disabled={disabled} onClick={() => onChange(null)}>
            {t('prefs.dice.texture.remove')}
          </Button>
        ) : null}
      </div>
      <p className={`rd-prefs__desc rd-prefs__textureHint${warning ? ' is-broken' : ''}`}>{warning ?? t('prefs.dice.texture.desc')}</p>
      {value ? (
        <div className="rd-prefs__textureScale">
          <label className="rd-prefs__swatchLabel" htmlFor={scaleId}>
            {t('prefs.dice.textureScale')}
          </label>
          <Slider
            id={scaleId}
            aria-describedby={`${scaleId}-desc`}
            value={Math.round(Math.log2(scale) * 10) / 10}
            min={Math.log2(DICE_TEXTURE_LIMITS.minScale)}
            max={Math.log2(DICE_TEXTURE_LIMITS.maxScale)}
            step={0.1}
            disabled={disabled}
            format={(v) => `${Math.round(2 ** v * 100)}%`}
            onChange={(v) => onScale(Math.round(2 ** v * 1000) / 1000)}
          />
          <p className="rd-prefs__desc" id={`${scaleId}-desc`}>
            {t('prefs.dice.textureScale.desc')}
          </p>
        </div>
      ) : null}
      {picking ? (
        <ImagePickerDialog
          cloud
          linkRoom={false}
          importWeb
          rules={TEXTURE_RULES}
          title={t('prefs.dice.texture.title')}
          onCancel={() => setPicking(false)}
          onPick={(ref) => {
            setPicking(false);
            onChange(ref);
          }}
        />
      ) : null}
    </div>
  );
}

function Swatches({ label, value, colors, disabled, onPick }: { label: string; value: string; colors: readonly string[]; disabled: boolean; onPick: (color: string) => void }) {
  const t = useT();
  const custom = !colors.includes(value.toLowerCase());
  return (
    <div className="rd-prefs__swatchGroup">
      <span className="rd-prefs__swatchLabel">{label}</span>
      <div role="radiogroup" aria-label={label} className="rd-prefs__swatches">
        {colors.map((color) => (
          <button
            key={color}
            type="button"
            role="radio"
            aria-checked={color === value.toLowerCase()}
            aria-label={`${label}: ${color}`}
            disabled={disabled}
            className={`rd-prefs__swatch${color === value.toLowerCase() ? ' is-on' : ''}`}
            style={{ background: color }}
            onClick={() => onPick(color)}
          />
        ))}
        <label className={`rd-prefs__swatch rd-prefs__swatch--custom${custom ? ' is-on' : ''}`} style={custom ? { background: value } : undefined} title={t('prefs.dice.otherColor')}>
          <input type="color" value={value} disabled={disabled} aria-label={`${label}: ${t('prefs.dice.otherColor')}`} onChange={(e) => onPick(e.target.value)} />
        </label>
      </div>
    </div>
  );
}

/** Aparência: usar as minhas cores, a cor do corpo e a do número, a textura, com a prévia. */
export function DiceLook() {
  const t = useT();
  const settings = useApp((s) => s.settings);
  const save = useSavePrefs();
  const custom = settings.diceCustom;
  const body = custom ? settings.diceBody : DEFAULT_SETTINGS.diceBody;
  const ink = custom ? settings.diceNumber : DEFAULT_SETTINGS.diceNumber;
  // a imagem já baixada para o atlas (o meu arquivo: o dono sempre abre)
  const preview = useDiceTexturePreview(settings.diceTexture);
  const shown = custom && preview !== 'error' ? preview : null;
  return (
    <div className="rd-prefs__diceLook">
      <div className="rd-prefs__dicePreview">
        <D20 body={body} ink={ink} texture={shown} scale={settings.diceTextureScale} label={t('prefs.dice.preview')} />
      </div>
      <div className="rd-prefs__diceFields">
        <div className="rd-prefs__inline">
          <div className="rd-prefs__rowText">
            <label htmlFor="pref-diceCustom" className="rd-prefs__label">
              {t('prefs.dice.use')}
            </label>
            <p className="rd-prefs__desc" id="pref-diceCustom-desc">
              {t('prefs.dice.use.desc')}
            </p>
          </div>
          <Switch id="pref-diceCustom" aria-describedby="pref-diceCustom-desc" checked={custom} onChange={(v) => save({ diceCustom: v })} />
        </div>
        <div className={custom ? undefined : 'is-off'}>
          <Swatches label={t('config.dice.body')} value={settings.diceBody} colors={BODY_SWATCHES} disabled={!custom} onPick={(c) => save({ diceBody: c })} />
          <Swatches label={t('config.dice.number')} value={settings.diceNumber} colors={NUMBER_SWATCHES} disabled={!custom} onPick={(c) => save({ diceNumber: c })} />
          <DiceTexture
            value={settings.diceTexture}
            preview={preview}
            scale={settings.diceTextureScale}
            disabled={!custom}
            onChange={(ref) => save({ diceTexture: ref })}
            onScale={(scale) => save({ diceTextureScale: scale })}
          />
        </div>
        <div>
          <Button variant="link" className="rd-prefs__link" disabled={!custom} onClick={() => save({ diceBody: DEFAULT_SETTINGS.diceBody, diceNumber: DEFAULT_SETTINGS.diceNumber })}>
            {t('prefs.dice.resetColors')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function sameRules(a: readonly DiceEffectRule[], b: readonly DiceEffectRule[]): boolean {
  return a.length === b.length && a.every((rule, i) => rule.sides === b[i]!.sides && rule.face === b[i]!.face && rule.effect === b[i]!.effect);
}

/** Valor em que o dado de teste cai para mostrar a regra. */
function previewValue(rule: DiceEffectRule): number {
  return rule.face === 'max' ? rule.sides : rule.face === 'min' ? 1 : rule.face;
}

/**
 * Efeitos especiais dos meus dados (docs/spec/dice-3d.md): uma linha por regra (dado, resultado,
 * efeito), o teste de cada uma no palco 3D e o padrão do RPG Desk (o d20 que cai no 20 pega fogo).
 * Igual ao padrão, grava `null`: volta a seguir o padrão, que pode mudar nas versões novas.
 */
export function DiceEffects() {
  const t = useT();
  const settings = useApp((s) => s.settings);
  const save = useSavePrefs();
  const [preview, setPreview] = useState<PreviewRequest | null>(null);
  const rules = settings.diceEffects ?? DEFAULT_DICE_EFFECTS;
  const setRules = (next: DiceEffectRule[]) => save({ diceEffects: sameRules(next, DEFAULT_DICE_EFFECTS) ? null : next });
  const change = (index: number, patch: Partial<DiceEffectRule>) => setRules(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  const faceOptions = (sides: number) => [
    { value: 'max', label: t('config.dice.effects.max', { n: sides }) },
    { value: 'min', label: t('config.dice.effects.min') },
    ...Array.from({ length: Math.max(0, sides - 2) }, (_, i) => ({ value: `v${i + 2}`, label: String(i + 2) })),
  ];
  const faceValue = (face: DiceEffectRule['face']) => (typeof face === 'number' ? `v${face}` : face);
  const parseFace = (value: string): DiceEffectRule['face'] => (value === 'max' || value === 'min' ? value : Number(value.slice(1)));
  const custom = settings.diceCustom;
  return (
    <div className="rd-prefs__effects">
      {rules.length ? (
        <ul className="rd-prefs__rules">
          {rules.map((rule, index) => (
            <li key={index} className="rd-prefs__rule">
              <span className="rd-prefs__desc">{t('prefs.dice.effects.when')}</span>
              <ComboBox
                aria-label={t('config.dice.effects.die')}
                value={String(rule.sides)}
                onChange={(v) => {
                  const sides = Number(v) as EffectDieSides;
                  // um valor que o dado novo não tem vira o maior
                  change(index, { sides, ...(typeof rule.face === 'number' && rule.face >= sides ? { face: 'max' as const } : {}) });
                }}
                options={EFFECT_DICE.map((sides) => ({ value: String(sides), label: `d${sides}` }))}
              />
              <ComboBox aria-label={t('config.dice.effects.face')} value={faceValue(rule.face)} onChange={(v) => change(index, { face: parseFace(v) })} options={faceOptions(rule.sides)} />
              <span className="rd-prefs__desc">{t('prefs.dice.effects.show')}</span>
              <ComboBox
                aria-label={t('config.dice.effects.effect')}
                value={rule.effect}
                onChange={(v) => change(index, { effect: v })}
                options={DICE_EFFECT_IDS.map((id) => ({ value: id as string, label: t(`dice.effect.${id}`) }))}
              />
              <span className="rd-prefs__ruleGap" />
              <Button
                variant="secondary"
                icon="play"
                disabled={!(DICE_EFFECT_IDS as readonly string[]).includes(rule.effect)}
                onClick={() => setPreview({ sides: rule.sides, value: previewValue(rule), effect: rule.effect as DiceEffectId, nonce: Date.now() })}
              >
                {t('config.dice.effects.preview')}
              </Button>
              <button type="button" className="rd-iconBtn" aria-label={t('config.dice.effects.remove')} title={t('config.dice.effects.remove')} onClick={() => setRules(rules.filter((_, i) => i !== index))}>
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="rd-prefs__desc">{t('config.dice.effects.none')}</p>
      )}
      <div className="rd-prefs__buttons">
        <Button variant="secondary" icon="plus" disabled={rules.length >= MAX_DICE_EFFECT_RULES} onClick={() => setRules([...rules, { sides: 20, face: 'max', effect: 'fire' }])}>
          {t('config.dice.effects.add')}
        </Button>
        <Button variant="link" className="rd-prefs__link" onClick={() => setRules([...DEFAULT_DICE_EFFECTS])}>
          {t('config.dice.effects.default')}
        </Button>
      </div>
      <DicePreview
        request={preview}
        body={custom ? settings.diceBody : DEFAULT_SETTINGS.diceBody}
        ink={custom ? settings.diceNumber : DEFAULT_SETTINGS.diceNumber}
        texture={custom ? settings.diceTexture : null}
        textureScale={settings.diceTextureScale}
        idleText={t('config.dice.effects.stage')}
        no3DText={t('config.dice.effects.no3d')}
        failedText={t('config.dice.effects.failed')}
      />
    </div>
  );
}
